import React, { useState } from "react";
import { useDispatch } from "react-redux";

import {
	Box,
	Button,
	FormControl,
	FormErrorMessage,
	FormLabel,
	HStack,
	Icon,
	Input,
	SimpleGrid,
	Text,
	Textarea,
	useColorModeValue,
	useToast,
	VStack,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { FiEdit2, FiSave, FiX } from "react-icons/fi";

import { updateOwnerAsync } from "../../redux/user/userSlice";

const MotionBox = motion(Box);

export default function BillingInformation({ owner }) {
	const initialOwnerInformation = {
		identificationNumber: owner.billingInformation.identificationNumber,
		address: owner.billingInformation.address,
		city: owner.billingInformation.city,
		country: owner.billingInformation.country,
		county: owner.billingInformation.county,
		ownerEmail: owner.email,
	};

	const [error, setError] = useState({});
	const [isEditing, setIsEditing] = useState(false);
	const [ownerInformation, setOwnerInformation] = useState(initialOwnerInformation);
	const dispatch = useDispatch();
	const toast = useToast();

	const bgColor = useColorModeValue("white", "gray.800");
	const borderColor = useColorModeValue("gray.200", "gray.700");
	const labelColor = useColorModeValue("gray.600", "gray.400");
	const valueColor = useColorModeValue("gray.800", "white");

	const handleSave = () => {
		dispatch(updateOwnerAsync({ type: "billingInformation", ...ownerInformation })).then((r) => {
			if (r.error?.message) {
				toast({
					title: "Error encountered",
					description: r.error.message,
					status: "error",
					duration: 9000,
					isClosable: true,
				});
			} else {
				toast({
					title: "Success!",
					description: "Billing information updated successfully",
					status: "success",
					duration: 2000,
					isClosable: true,
				});
				setIsEditing(false);
			}
		});
	};

	const handleOwnerInformationChange = (e) => {
		setOwnerInformation({ ...ownerInformation, [e.target.name]: e.target.value });
	};

	const handleCancel = () => {
		setOwnerInformation(initialOwnerInformation);
		setIsEditing(false);
		setError({});
	};

	return (
		<MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
			<Box p={6} bg={bgColor} borderRadius="lg" borderWidth="1px" borderColor={borderColor} boxShadow="sm">
				<HStack justify="space-between" mb={6}>
					<Text fontSize="lg" fontWeight="bold">
						Billing Information
					</Text>
					<Button
						size="sm"
						variant="ghost"
						colorScheme={isEditing ? "red" : "blue"}
						leftIcon={<Icon as={isEditing ? FiX : FiEdit2} />}
						onClick={() => {
							if (isEditing) {
								handleCancel();
							} else {
								setIsEditing(true);
							}
						}}
					>
						{isEditing ? "Cancel" : "Edit"}
					</Button>
				</HStack>

				<VStack spacing={6} align="stretch">
					<SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
						<FormControl isInvalid={error.identificationNumber}>
							<FormLabel color={labelColor}>CIF/CNP</FormLabel>
							{isEditing ? (
								<Input
									size="md"
									value={ownerInformation.identificationNumber}
									name="identificationNumber"
									onChange={handleOwnerInformationChange}
									placeholder="Enter CIF/CNP"
								/>
							) : (
								<Text color={valueColor}>{ownerInformation.identificationNumber || "-"}</Text>
							)}
							<FormErrorMessage>{error.identificationNumber}</FormErrorMessage>
						</FormControl>

						<FormControl isInvalid={error.city}>
							<FormLabel color={labelColor}>City</FormLabel>
							{isEditing ? (
								<Input size="md" value={ownerInformation.city} name="city" onChange={handleOwnerInformationChange} placeholder="Enter city" />
							) : (
								<Text color={valueColor}>{ownerInformation.city || "-"}</Text>
							)}
							<FormErrorMessage>{error.city}</FormErrorMessage>
						</FormControl>

						<FormControl isInvalid={error.county}>
							<FormLabel color={labelColor}>County</FormLabel>
							{isEditing ? (
								<Input size="md" value={ownerInformation.county} name="county" onChange={handleOwnerInformationChange} placeholder="Enter county" />
							) : (
								<Text color={valueColor}>{ownerInformation.county || "-"}</Text>
							)}
							<FormErrorMessage>{error.county}</FormErrorMessage>
						</FormControl>

						<FormControl isInvalid={error.country}>
							<FormLabel color={labelColor}>Country</FormLabel>
							{isEditing ? (
								<Input size="md" value={ownerInformation.country} name="country" onChange={handleOwnerInformationChange} placeholder="Enter country" />
							) : (
								<Text color={valueColor}>{ownerInformation.country || "-"}</Text>
							)}
							<FormErrorMessage>{error.country}</FormErrorMessage>
						</FormControl>
					</SimpleGrid>

					<FormControl isInvalid={error.address}>
						<FormLabel color={labelColor}>Address</FormLabel>
						{isEditing ? (
							<Textarea
								size="md"
								value={ownerInformation.address}
								name="address"
								onChange={handleOwnerInformationChange}
								placeholder="Enter complete address"
								rows={3}
							/>
						) : (
							<Text color={valueColor}>{ownerInformation.address || "-"}</Text>
						)}
						<FormErrorMessage>{error.address}</FormErrorMessage>
					</FormControl>

					{isEditing && (
						<Button colorScheme="blue" leftIcon={<Icon as={FiSave} />} onClick={handleSave} alignSelf="flex-start" size="md">
							Save Changes
						</Button>
					)}
				</VStack>
			</Box>
		</MotionBox>
	);
}
